import { useState, useEffect, useRef, useCallback } from 'react';

// Enchanted Forest Ambient Sound Generator
// Creates a mystical, flowing instrumental atmosphere
export const useAmbientSound = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(() => {
    return localStorage.getItem('zenermind_sound_muted') === 'true';
  });
  const audioContextRef = useRef(null);
  const nodesRef = useRef([]);
  const gainNodeRef = useRef(null);

  const createEnchantedForestSound = useCallback(() => {
    // Don't create if already exists and is running
    if (audioContextRef.current && audioContextRef.current.state === 'running') return;
    
    // Clean up closed context
    if (audioContextRef.current && audioContextRef.current.state === 'closed') {
      audioContextRef.current = null;
      nodesRef.current = [];
      gainNodeRef.current = null;
    }
    
    // Skip if already exists
    if (audioContextRef.current) return;
    
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
    audioContextRef.current = ctx;
    
    // Master gain with compression for smooth sound
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.knee.value = 30;
    compressor.ratio.value = 12;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;
    compressor.connect(ctx.destination);
    
    const masterGain = ctx.createGain();
    masterGain.gain.value = isMuted ? 0 : 0.25;
    masterGain.connect(compressor);
    gainNodeRef.current = masterGain;
    
    // === REVERB (Enchanted Cave Effect) ===
    const convolver = ctx.createConvolver();
    const reverbTime = 4;
    const sampleRate = ctx.sampleRate;
    const reverbLength = sampleRate * reverbTime;
    const impulse = ctx.createBuffer(2, reverbLength, sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const impulseData = impulse.getChannelData(channel);
      for (let i = 0; i < reverbLength; i++) {
        impulseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / reverbLength, 2.5);
      }
    }
    convolver.buffer = impulse;
    
    const reverbGain = ctx.createGain();
    reverbGain.gain.value = 0.4;
    convolver.connect(reverbGain);
    reverbGain.connect(masterGain);
    
    const dryGain = ctx.createGain();
    dryGain.gain.value = 0.6;
    dryGain.connect(masterGain);
    
    // === MYSTICAL PAD (Base Atmosphere) ===
    // Ethereal pad using detuned oscillators
    const padNotes = [130.81, 196.00, 261.63, 329.63]; // C3, G3, C4, E4 - mystical chord
    
    padNotes.forEach((freq, index) => {
      // Main oscillator
      const osc1 = ctx.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.value = freq;
      
      // Slightly detuned for richness
      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.value = freq * 1.003;
      
      const osc3 = ctx.createOscillator();
      osc3.type = 'triangle';
      osc3.frequency.value = freq * 0.998;
      
      // Gentle filter for warmth
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 800 + (index * 100);
      filter.Q.value = 0.5;
      
      // Volume envelope with slow LFO
      const oscGain = ctx.createGain();
      oscGain.gain.value = 0.08 - (index * 0.015);
      
      // Breathing modulation
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.05 + (index * 0.02);
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.03;
      lfo.connect(lfoGain);
      lfoGain.connect(oscGain.gain);
      
      osc1.connect(filter);
      osc2.connect(filter);
      osc3.connect(filter);
      filter.connect(oscGain);
      oscGain.connect(dryGain);
      oscGain.connect(convolver);
      
      osc1.start();
      osc2.start();
      osc3.start();
      lfo.start();
      
      nodesRef.current.push({ osc1, osc2, osc3, lfo });
    });
    
    // === MAGICAL ARPEGGIOS (Flowing Melody) ===
    const arpeggioNotes = [523.25, 659.25, 783.99, 659.25, 523.25, 392.00]; // C5, E5, G5...
    let arpIndex = 0;
    
    const arpOsc = ctx.createOscillator();
    arpOsc.type = 'sine';
    arpOsc.frequency.value = arpeggioNotes[0];
    
    const arpGain = ctx.createGain();
    arpGain.gain.value = 0;
    
    const arpFilter = ctx.createBiquadFilter();
    arpFilter.type = 'lowpass';
    arpFilter.frequency.value = 2000;
    arpFilter.Q.value = 2;
    
    arpOsc.connect(arpFilter);
    arpFilter.connect(arpGain);
    arpGain.connect(dryGain);
    arpGain.connect(convolver);
    arpOsc.start();
    
    // Arpeggio sequencer - gentle, flowing
    const playArpNote = () => {
      const now = ctx.currentTime;
      const note = arpeggioNotes[arpIndex % arpeggioNotes.length];
      
      // Smooth frequency glide
      arpOsc.frequency.setTargetAtTime(note, now, 0.1);
      
      // Soft attack and release
      arpGain.gain.setTargetAtTime(0.04, now, 0.15);
      arpGain.gain.setTargetAtTime(0, now + 1.5, 0.8);
      
      arpIndex++;
    };
    
    // Play notes at random intervals for organic feel
    const scheduleArp = () => {
      const interval = 2000 + Math.random() * 3000; // 2-5 seconds
      playArpNote();
      setTimeout(scheduleArp, interval);
    };
    setTimeout(scheduleArp, 3000);
    
    nodesRef.current.push({ osc: arpOsc });
    
    // === FOREST WIND (Filtered Noise) ===
    const windBufferSize = 2 * ctx.sampleRate;
    const windBuffer = ctx.createBuffer(2, windBufferSize, ctx.sampleRate);
    
    // Generate pink noise for natural wind
    for (let channel = 0; channel < 2; channel++) {
      const data = windBuffer.getChannelData(channel);
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < windBufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      }
    }
    
    const wind = ctx.createBufferSource();
    wind.buffer = windBuffer;
    wind.loop = true;
    
    // Animated filter for wind movement
    const windFilter = ctx.createBiquadFilter();
    windFilter.type = 'bandpass';
    windFilter.frequency.value = 400;
    windFilter.Q.value = 0.8;
    
    // LFO for wind sweep
    const windLFO = ctx.createOscillator();
    windLFO.type = 'sine';
    windLFO.frequency.value = 0.08;
    const windLFOGain = ctx.createGain();
    windLFOGain.gain.value = 300;
    windLFO.connect(windLFOGain);
    windLFOGain.connect(windFilter.frequency);
    
    const windGain = ctx.createGain();
    windGain.gain.value = 0.025;
    
    // Wind volume modulation (gusts)
    const gustLFO = ctx.createOscillator();
    gustLFO.type = 'sine';
    gustLFO.frequency.value = 0.03;
    const gustGain = ctx.createGain();
    gustGain.gain.value = 0.015;
    gustLFO.connect(gustGain);
    gustGain.connect(windGain.gain);
    
    wind.connect(windFilter);
    windFilter.connect(windGain);
    windGain.connect(dryGain);
    windGain.connect(convolver);
    
    wind.start();
    windLFO.start();
    gustLFO.start();
    
    nodesRef.current.push({ wind, windLFO, gustLFO });
    
    // === MYSTICAL CHIMES (Sparkle Effect) ===
    const chimeFreqs = [1318.51, 1567.98, 2093.00, 2637.02]; // E6, G6, C7, E7
    
    const playChime = () => {
      const freq = chimeFreqs[Math.floor(Math.random() * chimeFreqs.length)];
      
      const chimeOsc = ctx.createOscillator();
      chimeOsc.type = 'sine';
      chimeOsc.frequency.value = freq;
      
      const chimeGain = ctx.createGain();
      chimeGain.gain.value = 0;
      
      const chimeFilter = ctx.createBiquadFilter();
      chimeFilter.type = 'highpass';
      chimeFilter.frequency.value = 1000;
      
      chimeOsc.connect(chimeFilter);
      chimeFilter.connect(chimeGain);
      chimeGain.connect(convolver);
      
      const now = ctx.currentTime;
      chimeGain.gain.setTargetAtTime(0.015, now, 0.01);
      chimeGain.gain.setTargetAtTime(0, now + 0.1, 1.5);
      
      chimeOsc.start(now);
      chimeOsc.stop(now + 4);
    };
    
    // Random chimes
    const scheduleChime = () => {
      const interval = 4000 + Math.random() * 8000;
      playChime();
      setTimeout(scheduleChime, interval);
    };
    setTimeout(scheduleChime, 5000);
    
    // === SUB BASS (Grounding Presence) ===
    const subOsc = ctx.createOscillator();
    subOsc.type = 'sine';
    subOsc.frequency.value = 65.41; // C2
    
    const subGain = ctx.createGain();
    subGain.gain.value = 0.12;
    
    // Very slow modulation
    const subLFO = ctx.createOscillator();
    subLFO.type = 'sine';
    subLFO.frequency.value = 0.02;
    const subLFOGain = ctx.createGain();
    subLFOGain.gain.value = 0.04;
    subLFO.connect(subLFOGain);
    subLFOGain.connect(subGain.gain);
    
    const subFilter = ctx.createBiquadFilter();
    subFilter.type = 'lowpass';
    subFilter.frequency.value = 100;
    
    subOsc.connect(subFilter);
    subFilter.connect(subGain);
    subGain.connect(dryGain);
    
    subOsc.start();
    subLFO.start();
    
    nodesRef.current.push({ subOsc, subLFO });
    
    setIsPlaying(true);
    } catch (e) {
      // AudioContext creation failed, silently ignore
      console.warn('AudioContext creation failed:', e);
      audioContextRef.current = null;
    }
  }, [isMuted]);

  const stopSound = useCallback(() => {
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // Ignore close errors
      }
      audioContextRef.current = null;
      nodesRef.current = [];
      gainNodeRef.current = null;
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    localStorage.setItem('zenermind_sound_muted', newMuted.toString());
    
    if (gainNodeRef.current && audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        gainNodeRef.current.gain.setTargetAtTime(
          newMuted ? 0 : 0.25,
          audioContextRef.current.currentTime,
          0.5
        );
      } catch (e) {
        // Ignore errors
      }
    }
  }, [isMuted]);

  const startSound = useCallback(() => {
    // If context is closed or doesn't exist, create a new one
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      // Reset refs to ensure clean state
      audioContextRef.current = null;
      nodesRef.current = [];
      gainNodeRef.current = null;
      createEnchantedForestSound();
    } else if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {
        // If resume fails, recreate the context
        audioContextRef.current = null;
        nodesRef.current = [];
        gainNodeRef.current = null;
        createEnchantedForestSound();
      });
    }
  }, [createEnchantedForestSound]);

  useEffect(() => {
    return () => {
      stopSound();
    };
  }, [stopSound]);

  return { isPlaying, isMuted, startSound, stopSound, toggleMute };
};
